import React, { Component } from "react";
import Row from "reactstrap/lib/Row";
import Col from "reactstrap/lib/Col";
import { Button as RSButton, Collapse as RSCollapse, Card, CardBody } from 'reactstrap';
import { Table, TableHead, TableBody, TableRow, TableCell, Paper, IconButton, Switch, FormControlLabel, TablePagination } from '@material-ui/core';
import Icon from '@material-ui/core/Icon';
import InputFeild from "../../global/form/input"
import SelectList from "../../global/form/select"
import GoogleAddress from "../../global/google-address";
import ConfirmAction from "../../global/confirm-action";
import FieldLocationPolygonMap from "./field_location_polygon_map";
import ApiEndpoints from "../../../constants/api_endpoints";
import store from "../../../store";
import { setError, setSuccess, setLoader } from "../../../actions/action-creators";
import { Circle, Map, Marker, Polygon, InfoWindow } from "google-maps-react";

// location_type (CITY/PINCODE/AREA) is a display/organizational label.
// lat/long/range are the actual geofence data (center + radius in km) —
// same GoogleAddress + Circle pattern job_location already uses in
// manage_location.js, reused here since lead-mapping needs real
// coordinates, not just text.
//
// Code identifiers (class/methods/state/API namespace) stay "JobFieldLocation"
// to match the backend's job_field_location naming — only the strings a
// human actually sees say "Project", matching how the rest of the webapp UI
// already calls this entity "Project" (Manage Project, Project Info, etc.)
// even though the underlying prop/data is still named `job`.
const LOCATION_TYPES = [
    { value: "CITY", label: "City" },
    { value: "PINCODE", label: "Pincode" },
    { value: "AREA", label: "Area" },
];

const formatLocation = (location) => {
    if (location.location_type === "CITY") return location.city;
    if (location.location_type === "PINCODE") return `Pincode ${location.zip}, ${location.city}`;
    if (location.location_type === "AREA") return `${location.area_name}, Pincode ${location.zip}, ${location.city}`;
    return "-";
}

const SHAPE_TYPES = [
    { value: "CIRCLE", label: "Circle" },
    { value: "POLYGON", label: "Polygon" },
];

const ROWS_PER_PAGE = 5;

// Distinct hues so a glance at the map tells Circle vs Polygon apart without
// hovering anything — red for Circle (this app's existing geofence color),
// blue for Polygon (kept visually distinct, matches Google's own "new shape" blue).
// A location's own map_color (set manually or via KML import) always wins
// over this default — see [[project_fieldworker_survey]] for the migration
// that first populated it from a client's real per-area KML color-coding.
const SHAPE_COLORS = { CIRCLE: "#f1061c", POLYGON: "#1a73e8" };
const shapeColor = (location) => location.map_color || SHAPE_COLORS[location.shape_type] || SHAPE_COLORS.CIRCLE;

// polygon_points comes back from the backend as a JSON string (raw TEXT
// column); a freshly-drawn-but-unsaved one is already a plain array — this
// normalizes either shape for display/rendering.
const parsePolygonPoints = (raw) => {
    if (!raw) return [];
    if (Array.isArray(raw)) return raw;
    try { return JSON.parse(raw); } catch (e) { return []; }
}

// Plain arithmetic mean — same approximation as the backend's own
// api/job_field_location.js computeCentroid(), fine for a single polygon's
// small footprint. Just used to pick where to reverse-geocode from; the
// authoritative centroid stored in lat/long is still computed server-side.
const computeCentroid = (points) => {
    const sum = points.reduce((acc, p) => ({ lat: acc.lat + Number(p.lat), lng: acc.lng + Number(p.lng) }), { lat: 0, lng: 0 });
    return { lat: sum.lat / points.length, lng: sum.lng / points.length };
}

// Reverse-geocodes a point into { city, zip, area_name } — same
// address_components-by-type parsing convention as global/google-address.js's
// forward-search handlePlaceChanged(), just via Geocoder (point -> address)
// instead of Places Autocomplete (text -> place). Kept local to this file
// since it's the only POLYGON-mode consumer.
const reverseGeocodeCenter = (center) => new Promise((resolve, reject) => {
    if (!window.google || !window.google.maps) return reject(new Error("Maps not loaded"));
    new window.google.maps.Geocoder().geocode({ location: center }, (results, status) => {
        if (status !== "OK" || !results || !results.length) return reject(new Error("Geocode failed: " + status));
        const components = results[0].address_components.reduce((prev, cur) => {
            prev[cur.types[0]] = cur.long_name;
            return prev;
        }, {});
        resolve({
            city: components.administrative_area_level_3 || components.administrative_area_level_2,
            zip: components.postal_code,
            area_name: components.sublocality_level_1 || components.sublocality || components.neighborhood,
        });
    });
});

const degToRad = (deg) => deg * Math.PI / 180;
const radToDeg = (rad) => rad * 180 / Math.PI;
const distanceKm = (lat1, lng1, lat2, lng2) => {
    const radlat1 = Math.PI * lat1 / 180;
    const radlat2 = Math.PI * lat2 / 180;
    const radtheta = Math.PI * (lng1 - lng2) / 180;
    let dist = Math.sin(radlat1) * Math.sin(radlat2) + Math.cos(radlat1) * Math.cos(radlat2) * Math.cos(radtheta);
    dist = Math.acos(Math.min(1, Math.max(-1, dist))) * 180 / Math.PI * 60 * 1.1515 * 1.609344;
    return dist;
}
const getMapCenterAndZoom = (points) => {
    if (!points.length) return { center: { lat: 19.0760, lng: 72.8777 }, zoom: 11 };
    let sumX = 0, sumY = 0, sumZ = 0;
    points.forEach(p => {
        const lat = degToRad(p.lat), lng = degToRad(p.lng);
        sumX += Math.cos(lat) * Math.cos(lng);
        sumY += Math.cos(lat) * Math.sin(lng);
        sumZ += Math.sin(lat);
    });
    const avgX = sumX / points.length, avgY = sumY / points.length, avgZ = sumZ / points.length;
    const lng = Math.atan2(avgY, avgX);
    const lat = Math.atan2(avgZ, Math.sqrt(avgX * avgX + avgY * avgY));
    const center = { lat: radToDeg(lat), lng: radToDeg(lng) };
    const maxDist = Math.max(...points.map(p => distanceKm(center.lat, center.lng, p.lat, p.lng)), 1);
    const zoom = Math.min(14, Math.max(3, parseInt(2000 / maxDist)));
    return { center, zoom };
}

class ManageJobFieldLocations extends Component {
    constructor(props) {
        super(props);
        this.state = {
            jobFieldLocations: [],
            isOpenAdd: false,
            newLocation: {},
            editLocationId: null,
            editLocation: {},
            isOpenConfirm: false,
            isShowMap: false,
            leads: [],
            leadStartDate: "",
            leadEndDate: "",
            polygonDraw: null, // { location, onChange } while the draw-polygon modal is open
            selectedLocationId: null, // set by clicking a table row/map shape — stays highlighted/centered until another is clicked
            showLeadsOnMap: false, // lead pins + date filters are opt-in clutter, off by default
            infoWindowLocation: null, // the jobFieldLocations row currently shown in the map InfoWindow
            infoWindowMarker: null, // the underlying google.maps.Marker instance InfoWindow anchors to
            page: 0, // table pagination
            searchQuery: "", // filters the list (and map) by title/city/area/pincode
        }
    }

    // Case-insensitive substring match across the fields an admin would
    // actually recognize a location by — matters once a job has 70+ rows
    // (e.g. the Haldiram KML migration) and scrolling/paging isn't enough.
    filteredLocations = () => {
        const q = this.state.searchQuery.trim().toLowerCase();
        if (!q) return this.state.jobFieldLocations;
        return this.state.jobFieldLocations.filter(loc =>
            [loc.location_title, loc.city, loc.area_name, loc.zip, loc.address]
                .some(field => field && String(field).toLowerCase().includes(q))
        );
    }

    componentDidMount() {
        this.fetchJobFieldLocations();
        this.fetchLeads();
    }

    fetchJobFieldLocations = async () => {
        store.dispatch(setLoader(true));
        try {
            let resp = await ApiEndpoints.JobFieldLocation.fetch({ job_id: this.props.job.job_id });
            this.setState({ jobFieldLocations: resp.data.data });
        }
        catch (e) {
            store.dispatch(setError({ show: true, message: e.response ? e.response.data.message : e.toString() }))
        }
        store.dispatch(setLoader(false))
    }

    fetchLeads = async () => {
        try {
            let { leadStartDate, leadEndDate } = this.state;
            let params = { job_id: this.props.job.job_id };
            if (leadStartDate) params.start_date = leadStartDate;
            if (leadEndDate) params.end_date = leadEndDate;
            let resp = await ApiEndpoints.FieldSurveyLead.leadsWithLocationByJob(params);
            this.setState({ leads: resp.data.data });
        }
        catch (e) {
            store.dispatch(setError({ show: true, message: e.response ? e.response.data.message : e.toString() }))
        }
    }

    leadsCountForLocation = (job_field_location_id) => this.state.leads.filter(lead => lead.job_field_location_id === job_field_location_id).length

    // Clicking a table row: persistent highlight + map centers on it. Also
    // opens the map if it was hidden, so the highlight is actually visible.
    onLocationRowClick = (location) => this.setState({ selectedLocationId: location.job_field_location_id, isShowMap: true })

    // Clicking a marker both opens its info bubble AND selects/highlights it
    // in the table + map (same effect as clicking the table row).
    onMarkerClick = (location, props, marker) => {
        this.setState({ infoWindowLocation: location, infoWindowMarker: marker });
        this.onLocationRowClick(location);
    }
    onInfoWindowClose = () => this.setState({ infoWindowLocation: null, infoWindowMarker: null })

    openPolygonDraw = (location, onChange) => this.setState({ polygonDraw: { location, onChange } })
    closePolygonDraw = () => this.setState({ polygonDraw: null })
    // Applies shape_type/polygon_points SYNCHRONOUSLY — this used to await the
    // reverse-geocode call first, which left a real network-latency window
    // where clicking the outer "Save" button submitted the form before
    // shape_type/polygon_points had ever reached state, silently saving as a
    // default CIRCLE with nothing set. Geocoding is now a non-blocking
    // background enrichment that can only ever ADD city/zip/area_name later
    // (or fail silently) — it can no longer delay or race the actual save.
    savePolygonDraw = (points) => {
        const { location, onChange } = this.state.polygonDraw;
        onChange({ shape_type: "POLYGON", polygon_points: points });
        this.closePolygonDraw();
        reverseGeocodeCenter(computeCentroid(points))
            .then(addr => {
                const patch = {};
                // Only fill blanks — don't clobber anything the admin already typed.
                if (!location.city && addr.city) patch.city = addr.city;
                if (!location.zip && addr.zip) patch.zip = addr.zip;
                if (!location.area_name && addr.area_name) patch.area_name = addr.area_name;
                if (Object.keys(patch).length) onChange(patch);
            })
            .catch(() => {}); // best-effort only
    }

    // POLYGON shape must actually have its points committed before
    // submitting — e.g. drawing points then closing the modal via
    // Escape/backdrop-click (instead of "Save Shape") leaves shape_type set
    // to POLYGON from the dropdown alone, with no points ever saved.
    // Scoped to POLYGON only: a CIRCLE with no lat/long has always been a
    // valid, meaningful state (an informational entry with no geofence
    // restriction), so that's intentionally left unvalidated here — same
    // scope as the backend's own validateShapeOpts().
    // Returns an error string, or null if valid.
    validateShapeFields = (location) => {
        if ((location.shape_type || "CIRCLE") === "POLYGON" && parsePolygonPoints(location.polygon_points).length < 3) {
            return "Draw a polygon (at least 3 points) and click Save Shape before saving.";
        }
        return null;
    }

    addJobFieldLocation = async (e) => {
        e.preventDefault();
        let { newLocation } = this.state;
        const validationError = this.validateShapeFields(newLocation);
        if (validationError) return store.dispatch(setError({ show: true, message: validationError }));
        store.dispatch(setLoader(true));
        try {
            await ApiEndpoints.JobFieldLocation.add({ ...newLocation, job_id: this.props.job.job_id });
            await this.fetchJobFieldLocations();
            this.setState({ isOpenAdd: false, newLocation: {}, page: 0 });
            store.dispatch(setSuccess({ show: true, message: "Project Field Location added!" }))
        }
        catch (e) {
            store.dispatch(setError({ show: true, message: e.response ? e.response.data.message : e.toString() }))
        }
        store.dispatch(setLoader(false))
    }

    startEdit = (location) => {
        this.setState({ editLocationId: location.job_field_location_id, editLocation: { ...location } });
    }

    saveEdit = async (e) => {
        e.preventDefault();
        let { editLocationId, editLocation } = this.state;
        const validationError = this.validateShapeFields(editLocation);
        if (validationError) return store.dispatch(setError({ show: true, message: validationError }));
        store.dispatch(setLoader(true));
        try {
            await ApiEndpoints.JobFieldLocation.edit({ ...editLocation, job_field_location_id: editLocationId });
            await this.fetchJobFieldLocations();
            this.setState({ editLocationId: null, editLocation: {} });
            store.dispatch(setSuccess({ show: true, message: "Project Field Location updated!" }))
        }
        catch (e) {
            store.dispatch(setError({ show: true, message: e.response ? e.response.data.message : e.toString() }))
        }
        store.dispatch(setLoader(false))
    }

    deleteJobFieldLocation = async (job_field_location_id) => {
        store.dispatch(setLoader(true));
        try {
            await ApiEndpoints.JobFieldLocation.inactive({ job_field_location_id });
            await this.fetchJobFieldLocations();
            this.setState({ isOpenConfirm: false, page: 0 });
        }
        catch (e) {
            store.dispatch(setError({ show: true, message: e.response ? e.response.data.message : e.toString() }))
        }
        store.dispatch(setLoader(false))
    }

    // Spread across 3 rows so nothing feels cramped:
    // Row 1: Location Title + Type
    // Row 2: Address (wide, geofence center) + Range
    // Row 3: City + type-specific fields (Pincode for PINCODE/AREA, Area Name for AREA)
    renderLocationFields = (location, onChange) => {
        const shapeType = location.shape_type || "CIRCLE";
        const polygonPointCount = parsePolygonPoints(location.polygon_points).length;
        return (
        <>
            <Row className="align-items-center mb-3">
                <Col xs={4}>
                    <InputFeild required placeholder="Location Title" value={location.location_title || ""} onChange={(e) => onChange({ location_title: e.target.value })} />
                </Col>
                <Col xs={3}>
                    <SelectList placeholder="Location Type" value={location.location_type || ""} onChange={(e) => onChange({ location_type: e.target.value })}>
                        {LOCATION_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </SelectList>
                </Col>
                <Col xs={3}>
                    <SelectList placeholder="Shape" value={shapeType} onChange={(e) => onChange({ shape_type: e.target.value })}>
                        {SHAPE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </SelectList>
                </Col>
                <Col xs={2} className="d-flex align-items-center">
                    <input
                        type="color"
                        title="Map color"
                        value={shapeColor(location)}
                        onChange={(e) => onChange({ map_color: e.target.value })}
                        style={{ width: 32, height: 32, padding: 0, border: "1px solid #ccc", borderRadius: 4, cursor: "pointer" }}
                    />
                    <span className="text-muted ml-1" style={{ fontSize: 11 }}>Color</span>
                </Col>
            </Row>
            {shapeType === "POLYGON" ?
                <Row className="align-items-center mb-3">
                    <Col xs={12}>
                        <RSButton type="button" color="secondary" size="sm" onClick={() => this.openPolygonDraw(location, onChange)}>
                            <Icon className="align-middle mr-1" style={{ fontSize: 16 }}>edit_location</Icon>
                            {polygonPointCount > 0 ? `Edit Polygon (${polygonPointCount} points)` : "Draw Polygon"}
                        </RSButton>
                    </Col>
                </Row>
                :
                <Row className="align-items-center mb-3">
                    <Col xs={9}>
                        <GoogleAddress
                            defaultValue={location.address}
                            placeholder="Search address to set geofence center"
                            onPlaceChange={(place) => onChange({ address: place.address, city: place.city, zip: place.zip, lat: place.lat, long: place.long })}
                        />
                    </Col>
                    <Col xs={2}>
                        <InputFeild required type="number" placeholder="Range (Km)" value={location.range || ""} onChange={(e) => onChange({ range: e.target.value })} />
                    </Col>
                </Row>
            }
            <Row className="align-items-center">
                <Col xs={3}>
                    <InputFeild required placeholder="City" value={location.city || ""} onChange={(e) => onChange({ city: e.target.value })} />
                </Col>
                {(location.location_type === "PINCODE" || location.location_type === "AREA") &&
                    <Col xs={3}>
                        <InputFeild required placeholder="Pincode" value={location.zip || ""} onChange={(e) => onChange({ zip: e.target.value })} />
                    </Col>
                }
                {location.location_type === "AREA" &&
                    <Col xs={3}>
                        <InputFeild required placeholder="Area / Market Name" value={location.area_name || ""} onChange={(e) => onChange({ area_name: e.target.value })} />
                    </Col>
                }
            </Row>
        </>
        );
    }

    // Extracted so the same table can sit full-width (map hidden) or in a
    // half-width column next to the map (map shown) without duplicating JSX.
    renderTable = () => {
        let { editLocationId, editLocation, selectedLocationId, page, searchQuery } = this.state;
        const jobFieldLocations = this.filteredLocations();
        const pagedLocations = jobFieldLocations.slice(page * ROWS_PER_PAGE, page * ROWS_PER_PAGE + ROWS_PER_PAGE);
        // flex:1 (not a fixed height) fills whatever height the split-view Row
        // stretches this column to — the Row is display:flex by default, so its
        // two columns are already equal-height, matching the map's height exactly
        // without duplicating a magic-number height in two places.
        return (
            <Paper elevation={1} style={this.state.isShowMap ? { overflowY: "auto", maxWidth: "100%", flex: 1, minHeight: 0 } : { overflowY: "auto", maxWidth: "100%" }}>
                {/* Fixed layout + explicit % widths so all 4 columns always fit and
                    wrap text instead of relying on horizontal scroll — on macOS a
                    scrollbar that only appears while actively scrolling made the
                    Leads/Action columns effectively invisible in the half-width layout. */}
                <Table size="small" style={{ tableLayout: "fixed", width: "100%" }}>
                    <TableHead>
                        <TableRow style={{ background: "#fafafa" }}>
                            <TableCell style={{ fontWeight: 600, width: "38%" }}>Title</TableCell>
                            <TableCell style={{ fontWeight: 600, width: "32%" }}>Geofence</TableCell>
                            <TableCell style={{ fontWeight: 600, width: "10%" }} align="right">Leads</TableCell>
                            <TableCell style={{ fontWeight: 600, width: "20%" }} align="right">Action</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {!jobFieldLocations.length ?
                            <TableRow>
                                <TableCell colSpan={4} align="center">
                                    <em style={{ color: "#999" }}>
                                        {searchQuery ? `No locations match "${searchQuery}"` : "No Project Field Locations added yet"}
                                    </em>
                                </TableCell>
                            </TableRow>
                            :
                            pagedLocations.map(location =>
                                editLocationId === location.job_field_location_id ?
                                    <TableRow key={location.job_field_location_id}>
                                        <TableCell colSpan={4}>
                                            <form onSubmit={this.saveEdit}>
                                                {this.renderLocationFields(editLocation, (patch) => this.setState({ editLocation: { ...editLocation, ...patch } }))}
                                                <Row className="justify-content-end mt-3">
                                                    <Col xs="auto">
                                                        <IconButton size="small" type="submit"><Icon style={{ fontSize: 18 }}>done</Icon></IconButton>
                                                        <IconButton size="small" type="button" onClick={() => this.setState({ editLocationId: null, editLocation: {} })}><Icon style={{ fontSize: 18 }}>close</Icon></IconButton>
                                                    </Col>
                                                </Row>
                                            </form>
                                        </TableCell>
                                    </TableRow>
                                    :
                                    <TableRow
                                        key={location.job_field_location_id}
                                        hover
                                        selected={selectedLocationId === location.job_field_location_id}
                                        style={{ cursor: "pointer" }}
                                        onClick={() => this.onLocationRowClick(location)}
                                    >
                                        <TableCell>
                                            <span className="d-block">{location.location_title}</span>
                                            <span className="d-block text-muted" style={{ fontSize: 11 }}>{formatLocation(location)}</span>
                                        </TableCell>
                                        <TableCell>
                                            <span className="d-block">
                                                <span style={{ width: 9, height: 9, borderRadius: "50%", background: shapeColor(location), display: "inline-block", marginRight: 5 }} />
                                                {location.shape_type === "POLYGON" ? "Polygon" : "Circle"}
                                            </span>
                                            {location.lat && location.long &&
                                                <span className="d-block text-muted" style={{ fontSize: 11 }}>{parseFloat(location.lat).toFixed(4)}, {parseFloat(location.long).toFixed(4)}</span>
                                            }
                                            <span className="d-block text-muted" style={{ fontSize: 11 }}>
                                                {location.shape_type === "POLYGON"
                                                    ? `${parsePolygonPoints(location.polygon_points).length}-point boundary`
                                                    : location.range ? `${location.range} km radius` : "No geofence set"}
                                            </span>
                                        </TableCell>
                                        <TableCell align="right">{this.leadsCountForLocation(location.job_field_location_id)}</TableCell>
                                        <TableCell align="right">
                                            <IconButton size="small" onClick={(e) => { e.stopPropagation(); this.startEdit(location); }}><Icon style={{ fontSize: 18 }}>edit</Icon></IconButton>
                                            <IconButton size="small" onClick={(e) => { e.stopPropagation(); this.setState({ isOpenConfirm: () => this.deleteJobFieldLocation(location.job_field_location_id) }); }}><Icon style={{ fontSize: 18 }}>delete</Icon></IconButton>
                                        </TableCell>
                                    </TableRow>
                            )
                        }
                    </TableBody>
                </Table>
                {jobFieldLocations.length > ROWS_PER_PAGE &&
                    <TablePagination
                        rowsPerPageOptions={[ROWS_PER_PAGE]}
                        component="div"
                        count={jobFieldLocations.length}
                        rowsPerPage={ROWS_PER_PAGE}
                        page={page}
                        onChangePage={(e, newPage) => this.setState({ page: newPage })}
                    />
                }
            </Paper>
        );
    }

    render() {
        let { isOpenAdd, newLocation, isShowMap, leads, leadStartDate, leadEndDate, selectedLocationId, infoWindowLocation, infoWindowMarker, showLeadsOnMap, searchQuery } = this.state;
        // Search filters the map shapes/markers too, not just the table —
        // with 70+ locations (e.g. a KML-migrated job) finding one visually
        // needs the same narrowing the table gets.
        const jobFieldLocations = this.filteredLocations();
        // Leads only count toward the auto-fit view when actually shown —
        // otherwise a far-off lead you can't even see would zoom the map out needlessly.
        const { center: autoCenter, zoom: autoZoom } = getMapCenterAndZoom([
            ...jobFieldLocations.map(loc => ({ lat: parseFloat(loc.lat), lng: parseFloat(loc.long) })),
            ...(showLeadsOnMap ? leads.map(lead => ({ lat: parseFloat(lead.lat), lng: parseFloat(lead.lng) })) : [])
        ].filter(p => !isNaN(p.lat) && !isNaN(p.lng)));
        // Selecting a row (click) re-centers/zooms the map onto just that
        // location, overriding the auto-fit-everything default.
        const selectedLocation = jobFieldLocations.find(loc => loc.job_field_location_id === selectedLocationId);
        const selectedHasCoords = selectedLocation && !isNaN(parseFloat(selectedLocation.lat)) && !isNaN(parseFloat(selectedLocation.long));
        const mapCenter = selectedHasCoords ? { lat: parseFloat(selectedLocation.lat), lng: parseFloat(selectedLocation.long) } : autoCenter;
        const mapZoom = selectedHasCoords ? 13 : autoZoom;
        return (
            <div>
                <Row className="align-items-center mb-3">
                    <Col xs="auto">
                        <RSButton className="buttonred" style={{ height: "30px", paddingTop: "4px" }} onClick={() => this.setState({ isOpenAdd: true })}>
                            <Icon className="align-middle mr-1" style={{ fontSize: 16 }}>add_location</Icon> Add Project Field Location
                        </RSButton>
                    </Col>
                    <Col xs={12} md={4} className="ml-md-auto mt-2 mt-md-0">
                        <InputFeild
                            placeholder="Search by title, city, area or pincode..."
                            value={searchQuery}
                            onChange={(e) => this.setState({ searchQuery: e.target.value, page: 0 })}
                        />
                    </Col>
                    <Col xs="auto" className="ml-2">
                        <Icon style={{ cursor: "pointer" }} onClick={() => this.setState({ isShowMap: !isShowMap })}>map</Icon>
                    </Col>
                </Row>
                <RSCollapse isOpen={isOpenAdd}>
                    <Card className="mb-3 shadow-sm">
                        <CardBody>
                            <form onSubmit={this.addJobFieldLocation}>
                                {this.renderLocationFields(newLocation, (patch) => this.setState({ newLocation: { ...newLocation, ...patch } }))}
                                <Row className="justify-content-end mt-3">
                                    <Col xs="auto">
                                        <RSButton className="buttonred mr-2" style={{ height: "30px", paddingTop: "4px" }}>Save</RSButton>
                                        <RSButton onClick={() => this.setState({ isOpenAdd: false, newLocation: {} })} type="button" color="secondary" style={{ height: "30px", paddingTop: "4px" }}>Cancel</RSButton>
                                    </Col>
                                </Row>
                            </form>
                        </CardBody>
                    </Card>
                </RSCollapse>
                {!isShowMap && this.renderTable()}
                {isShowMap &&
                    <Row noGutters>
                        <Col xs={12} md={6} className="mb-3 pr-md-2 d-flex flex-column">
                            {this.renderTable()}
                        </Col>
                        <Col xs={12} md={6} className="mb-3 pl-md-2">
                            <Row noGutters className="align-items-center mb-2" style={{ fontSize: 12 }}>
                                <Col xs="auto" className="d-flex align-items-center">
                                    <span style={{ width: 10, height: 10, borderRadius: "50%", background: SHAPE_COLORS.CIRCLE, display: "inline-block", marginRight: 4 }} /> Circle
                                </Col>
                                <Col xs="auto" className="d-flex align-items-center ml-3">
                                    <span style={{ width: 10, height: 10, background: SHAPE_COLORS.POLYGON, display: "inline-block", marginRight: 4 }} /> Polygon
                                </Col>
                                <Col xs="auto" className="d-flex align-items-center ml-3">
                                    <img src="/pin_green.svg" alt="" style={{ width: 12, height: 12, marginRight: 4 }} /> Lead
                                </Col>
                                <Col xs="auto" className="ml-2">
                                    <FormControlLabel
                                        style={{ marginLeft: 0 }}
                                        control={<Switch size="small" checked={showLeadsOnMap} onChange={(e) => this.setState({ showLeadsOnMap: e.target.checked })} />}
                                        label="Show Leads"
                                    />
                                </Col>
                                {selectedLocationId &&
                                    <Col xs="auto" className="ml-auto">
                                        <RSButton type="button" color="secondary" size="sm" onClick={() => this.setState({ selectedLocationId: null })}>Reset view</RSButton>
                                    </Col>
                                }
                            </Row>
                            {showLeadsOnMap &&
                                <Row noGutters className="align-items-center mb-2">
                                    <Col xs={5}>
                                        <InputFeild type="date" placeholder="Leads From" value={leadStartDate} onChange={(e) => this.setState({ leadStartDate: e.target.value }, this.fetchLeads)} />
                                    </Col>
                                    <Col xs={5} className="ml-2">
                                        <InputFeild type="date" placeholder="Leads To" value={leadEndDate} onChange={(e) => this.setState({ leadEndDate: e.target.value }, this.fetchLeads)} />
                                    </Col>
                                    {(leadStartDate || leadEndDate) &&
                                        <Col xs="auto" className="ml-2">
                                            <RSButton type="button" color="secondary" size="sm" onClick={() => this.setState({ leadStartDate: "", leadEndDate: "" }, this.fetchLeads)}>Clear</RSButton>
                                        </Col>
                                    }
                                </Row>
                            }
                            <div style={{ height: 420 }}>
                                <Map initialCenter={mapCenter} center={mapCenter} google={window.google} style={{ width: "100%", height: 420, position: "relative" }} zoom={mapZoom} onClick={this.onInfoWindowClose}>
                                    {jobFieldLocations.map(loc => {
                                        // Stroke/fill stay CONSTANT regardless of hover — google-maps-react's
                                        // Circle recreates its native overlay on ANY prop change (not just
                                        // geometry), so varying style by hover state caused it to be torn down
                                        // and rebuilt on every hover transition, which was part of the flicker/
                                        // wrong-location-highlighted bug. Highlight now shows via the marker
                                        // InfoWindow and the row/map click-to-zoom instead of restyling shapes.
                                        const color = shapeColor(loc);
                                        const shapeProps = {
                                            key: loc.job_field_location_id,
                                            strokeColor: color,
                                            strokeOpacity: 1,
                                            strokeWeight: 2,
                                            fillColor: color,
                                            fillOpacity: 0.3,
                                            onClick: () => this.onLocationRowClick(loc)
                                        };
                                        return loc.shape_type === "POLYGON" ?
                                            <Polygon {...shapeProps} paths={parsePolygonPoints(loc.polygon_points)} />
                                            :
                                            <Circle {...shapeProps} radius={loc.range * 1000} center={{ lat: parseFloat(loc.lat), lng: parseFloat(loc.long) }} />;
                                    })}
                                    {jobFieldLocations.map(loc =>
                                        // Icon size stays constant regardless of interaction — google-maps-react
                                        // treats `icon` prop changes as a full re-render, tearing down and
                                        // recreating the native marker, which previously caused the InfoWindow to
                                        // flicker/reattach to the wrong location when tied to hover state.
                                        <Marker
                                            key={"loc-" + loc.job_field_location_id}
                                            position={{ lat: parseFloat(loc.lat), lng: parseFloat(loc.long) }}
                                            onClick={(props, marker) => this.onMarkerClick(loc, props, marker)}
                                            icon={{
                                                url: loc.shape_type === "POLYGON" ? "/pin_blue.svg" : "/pin_red.svg",
                                                scaledSize: new window.google.maps.Size(32, 32)
                                            }}
                                        />
                                    )}
                                    {showLeadsOnMap && leads.map(lead =>
                                        <Marker
                                            key={"lead-" + lead.form_response_id}
                                            position={{ lat: parseFloat(lead.lat), lng: parseFloat(lead.lng) }}
                                            title="Lead"
                                            icon={{
                                                url: "/pin_green.svg",
                                                scaledSize: new window.google.maps.Size(24, 24)
                                            }}
                                        />
                                    )}
                                    <InfoWindow marker={infoWindowMarker} visible={!!infoWindowLocation} onClose={this.onInfoWindowClose}>
                                        <div style={{ fontSize: 13, minWidth: 160 }}>
                                            {infoWindowLocation &&
                                                <>
                                                    <strong>{infoWindowLocation.location_title}</strong>
                                                    <div className="text-muted">{formatLocation(infoWindowLocation)}</div>
                                                    <div className="mt-1">
                                                        {infoWindowLocation.shape_type === "POLYGON" ? "Polygon" : "Circle"} —{" "}
                                                        {infoWindowLocation.shape_type === "POLYGON"
                                                            ? `${parsePolygonPoints(infoWindowLocation.polygon_points).length}-point boundary`
                                                            : infoWindowLocation.range ? `${infoWindowLocation.range} km radius` : "no geofence set"}
                                                    </div>
                                                    <div>{this.leadsCountForLocation(infoWindowLocation.job_field_location_id)} lead{this.leadsCountForLocation(infoWindowLocation.job_field_location_id) === 1 ? "" : "s"}</div>
                                                </>
                                            }
                                        </div>
                                    </InfoWindow>
                                </Map>
                            </div>
                        </Col>
                    </Row>
                }
                <ConfirmAction isOpen={!!this.state.isOpenConfirm} callback={this.state.isOpenConfirm} onClose={() => this.setState({ isOpenConfirm: false })} />
                <FieldLocationPolygonMap
                    open={!!this.state.polygonDraw}
                    initialPoints={this.state.polygonDraw ? parsePolygonPoints(this.state.polygonDraw.location.polygon_points) : []}
                    initialCenter={
                        this.state.polygonDraw && !isNaN(parseFloat(this.state.polygonDraw.location.lat))
                            ? { lat: parseFloat(this.state.polygonDraw.location.lat), lng: parseFloat(this.state.polygonDraw.location.long) }
                            : mapCenter
                    }
                    onClose={this.closePolygonDraw}
                    onSave={this.savePolygonDraw}
                />
            </div>
        );
    }
}
export default ManageJobFieldLocations;
